import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { type MouseEvent, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import IconTile from '~/components/IconTile';
import { type PanelFrameSize } from '~/components/toolbar/shared/PanelFrame';
import { type PanelDragListeners } from '~/components/toolbar/shared/usePanelDrag';

interface PanelHeaderProps {
    title: string;
    /** The panel's icon, shown in a tinted tile before the title. */
    icon?: ReactNode;
    dragListeners: PanelDragListeners;
    onClose: () => void;
    closeDisabled?: boolean;
    /** Names what the close control does. By default it reads "Close". */
    closeLabel?: string;
    /** Controls shown before the close control, outside the drag handle. */
    actions?: ReactNode;
    /** The size of the panel the header heads; `medium` unless given. */
    size?: PanelFrameSize;
}

/**
 * Header of a draggable panel: the title block is the drag handle, the
 * close control stays outside it so a press there never arms a drag. The
 * handle takes the focus, and its key listeners in `dragListeners` move the
 * panel.
 */
export default function PanelHeader({
    title,
    icon,
    dragListeners,
    onClose,
    closeDisabled = false,
    closeLabel,
    actions,
    size = 'medium',
}: PanelHeaderProps) {
    const intl = useIntl();
    const closeText = closeLabel ?? intl.formatMessage({ id: 'close' });

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flex: 1,
                minWidth: 0,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={size === 'small' ? 0.5 : 1.5}
                flex={1}
                minWidth={0}
                {...dragListeners}
                tabIndex={0}
                role="button"
                aria-label={intl.formatMessage({ id: 'panel.move' })}
                data-drag-handle
                sx={{
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    userSelect: 'none',
                    touchAction: 'none',
                }}
                title={intl.formatMessage({ id: 'panel.dragToMove' })}
            >
                {icon && <IconTile size={size}>{icon}</IconTile>}
                <Typography
                    fontSize={size === 'small' ? 12 : 15}
                    fontWeight={size === 'small' ? 500 : 400}
                    noWrap
                    data-testid="panel-header-title"
                >
                    {title}
                </Typography>
            </Stack>
            {actions}
            <PanelHeaderButton
                label={closeText}
                onClick={onClose}
                disabled={closeDisabled}
                size={size}
            >
                <X size={16} />
            </PanelHeaderButton>
        </Box>
    );
}

/** An icon control in a panel header, named by its tooltip. */
export function PanelHeaderButton({
    label,
    onClick,
    disabled = false,
    size = 'medium',
    children,
}: {
    label: string;
    onClick: (event: MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    /** A small header takes a 24 px control. */
    size?: PanelFrameSize;
    children: ReactNode;
}) {
    return (
        <Tooltip title={label} placement="left">
            <span style={{ flexShrink: 0 }}>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    sx={{ p: size === 'small' ? 0.5 : 0.75 }}
                    aria-label={label}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}
